import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellOff, CheckCheck, Loader2, Trash2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getBackendUrl } from '../utils/getBackendUrl';
import { updateAppBadge } from '../utils/pushNotifications';

const API = `${getBackendUrl()}/api`;

const timeAgo = (dateString) => {
  const diffMs = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString('en-CA');
};

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [swipe, setSwipe] = useState({}); // id -> offset px
  const touchRef = React.useRef({ id: null, startX: 0, offset: 0 });

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`);
      setNotifications(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Could not load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotifications(); }, []);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      setNotifications((list) => list.map((n) => ({ ...n, is_read: true })));
      updateAppBadge();
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Could not mark all read');
    }
  };

  const handleClick = async (n) => {
    if (!n.is_read) {
      setNotifications((list) => list.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      axios.put(`${API}/notifications/${n.id}/read`).then(() => updateAppBadge()).catch(() => {});
    }
    if (n.link) navigate(n.link);
  };

  const deleteNotification = async (id) => {
    setNotifications((list) => list.filter((x) => x.id !== id));
    setSwipe((s) => ({ ...s, [id]: 0 }));
    try {
      await axios.delete(`${API}/notifications/${id}`);
      updateAppBadge();
      toast.success('Alert deleted');
    } catch {
      toast.error('Could not delete alert');
      fetchNotifications();
    }
  };

  const onTouchStart = (id) => (e) => {
    touchRef.current = { id, startX: e.touches[0].clientX, offset: swipe[id] || 0 };
  };

  const onTouchMove = (id) => (e) => {
    if (touchRef.current.id !== id) return;
    const dx = e.touches[0].clientX - touchRef.current.startX + touchRef.current.offset;
    setSwipe((s) => ({ ...s, [id]: Math.min(0, dx) }));
  };

  const onTouchEnd = (id) => () => {
    const offset = swipe[id] || 0;
    if (offset < -90) {
      deleteNotification(id);
    } else if (offset < -40) {
      setSwipe((s) => ({ ...s, [id]: -72 })); // snap open to reveal delete button
    } else {
      setSwipe((s) => ({ ...s, [id]: 0 }));
    }
  };

  return (
    <div className="page-container" data-testid="notifications-page">
      <div className="glass-card p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-500/20 rounded-lg">
              <Bell className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Notifications</h1>
              {unreadCount > 0 && <p className="text-sm text-slate-400">{unreadCount} unread</p>}
            </div>
          </div>
          {unreadCount > 0 && (
            <Button
              onClick={markAllRead}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 min-h-[44px]"
              data-testid="notifications-mark-all-read"
            >
              <CheckCheck className="w-4 h-4 mr-2" /> Mark all read
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-14 text-slate-400">
            <BellOff className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-semibold">You're all caught up!</p>
            <p className="text-sm">New alerts from the site will show up here</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((n) => (
              <div key={n.id} className="relative overflow-hidden rounded-xl" data-testid={`notification-row-${n.id}`}>
                {/* Red delete layer revealed on swipe */}
                {(swipe[n.id] || 0) < 0 && (
                <div className="absolute inset-0 bg-red-600 rounded-xl flex items-center justify-end pr-5">
                  <button
                    onClick={() => deleteNotification(n.id)}
                    className="text-white flex flex-col items-center gap-0.5"
                    data-testid={`swipe-delete-${n.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                    <span className="text-[10px] font-bold">Delete</span>
                  </button>
                </div>
                )}
                <div
                  className="relative"
                  style={{ transform: `translateX(${swipe[n.id] || 0}px)`, transition: touchRef.current.id === n.id ? 'none' : 'transform 0.2s ease' }}
                  onTouchStart={onTouchStart(n.id)}
                  onTouchMove={onTouchMove(n.id)}
                  onTouchEnd={onTouchEnd(n.id)}
                >
                  <button
                    onClick={() => { if ((swipe[n.id] || 0) < -10) { setSwipe((s) => ({ ...s, [n.id]: 0 })); return; } handleClick(n); }}
                    className={`w-full text-left rounded-xl p-4 border transition-colors flex items-start gap-3 ${
                      n.is_read
                        ? 'bg-slate-800/40 border-slate-700 hover:bg-slate-800/70'
                        : 'bg-blue-500/10 border-blue-500/40 hover:bg-blue-500/20'
                    }`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className={`p-2 rounded-full flex-shrink-0 ${n.is_read ? 'bg-slate-700' : 'bg-blue-600'}`}>
                      <Bell className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${n.is_read ? 'font-semibold text-slate-300' : 'font-bold text-white'}`}>{n.title}</p>
                      {n.message && <p className="text-sm text-slate-400 line-clamp-2">{n.message}</p>}
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-slate-500">{timeAgo(n.created_at)}</p>
                        {n.is_read && (
                          <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-400" data-testid={`read-label-${n.id}`}>
                            <Check className="w-3 h-3" /> Read
                          </span>
                        )}
                      </div>
                    </div>
                    {!n.is_read && <span className="w-2.5 h-2.5 bg-blue-500 rounded-full flex-shrink-0 mt-2" />}
                    <span className="hidden sm:block w-8 flex-shrink-0" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                    className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-slate-500 hover:text-red-400 hover:bg-red-500/10"
                    title="Delete alert"
                    data-testid={`delete-notification-${n.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            <p className="text-center text-[11px] text-slate-500 pt-2 sm:hidden">Swipe left on an alert to delete it</p>
          </div>
        )}
      </div>
    </div>
  );
}
